// A financial statement's ACCOUNTING BASIS (ADR-0053) as every surface words it:
// derived from the document by the AI ("Basis: Accrual (from the document)"),
// or corrected by the room admin / the seller. A fact about the document — it
// never changes who can see it.
import type { DocumentStatementBasis, StatementBasis, StatementBasisSource } from '../api/types';

/** The bases in the order a person chooses among them. */
export const STATEMENT_BASIS_OPTIONS: readonly StatementBasis[] = [
  'accrual',
  'cash',
  'modified_cash',
  'not_stated',
];

export const STATEMENT_BASIS_LABELS: Record<StatementBasis, string> = {
  accrual: 'Accrual',
  cash: 'Cash',
  modified_cash: 'Modified cash (tax basis)',
  not_stated: 'Not stated',
};

const SOURCE_LABELS: Record<StatementBasisSource, string> = {
  ai: 'from the document',
  seller: 'set by the seller',
  admin: 'set by the room admin',
};

/** "from the document", "set by the seller", "set by the room admin". */
export function statementBasisSourceLabel(source: StatementBasisSource): string {
  return SOURCE_LABELS[source];
}

/** "Accrual (from the document)". Pure. */
export function statementBasisText(value: DocumentStatementBasis): string {
  return `${STATEMENT_BASIS_LABELS[value.basis]} (${statementBasisSourceLabel(value.source)})`;
}

/**
 * The admin's extra word on an AI answer: its confidence, and — for a "not
 * stated" the classifier was unsure about — that it was unsure rather than that
 * the document is silent. Null for a person's answer or when no confidence was
 * reported. Pure.
 */
export function statementBasisConfidenceNote(
  value: DocumentStatementBasis,
  sureAt = 0.75,
): string | null {
  if (value.source !== 'ai' || value.confidence === null) return null;
  const pct = `${Math.round(value.confidence * 100)}%`;
  return value.basis === 'not_stated' && value.confidence < sureAt
    ? `the AI was not sure (${pct}), so it did not guess`
    : `${pct} confidence`;
}

/** How many of the documents carry each basis — the data room's basis filter. Pure. */
export function countStatementBases(
  documents: ReadonlyArray<{ statementBasis?: DocumentStatementBasis | null }>,
): Record<StatementBasis, number> {
  const counts: Record<StatementBasis, number> = {
    accrual: 0,
    cash: 0,
    modified_cash: 0,
    not_stated: 0,
  };
  for (const d of documents) if (d.statementBasis) counts[d.statementBasis.basis] += 1;
  return counts;
}

// The server's financial-statement category test (classification/statement-basis.ts),
// mirrored for ONE purpose: offering "Set the basis" on a document a person filed
// as a statement that the AI never asked about. A UI hint only — the server
// accepts a correction on any library document.
const STATEMENT_NAME =
  /\b(?:profit\s*(?:and|&)\s*loss|p\s*&\s*l|income\s+statements?|statements?\s+of\s+(?:income|operations|financial\s+position|cash\s+flows?)|balance\s+sheets?|cash\s*flows?(?:\s+statements?)?|financial\s+statements?)\b/i;
const NOT_A_STATEMENT = /\b(?:projections?|forecasts?|budgets?|pro\s*forma|off[-\s]balance)\b/i;

/** Whether a filing path (root → deepest) reads as a financial statement. Pure. */
export function isFinancialStatementPath(path: readonly string[]): boolean {
  const deepest = path.at(-1);
  if (deepest === undefined || NOT_A_STATEMENT.test(deepest)) return false;
  return path.some((name) => STATEMENT_NAME.test(name) && !NOT_A_STATEMENT.test(name));
}

/** Whether the basis belongs on this document's details: it has one, or it is filed as a statement. Pure. */
export function showsStatementBasis(document: {
  statementBasis?: DocumentStatementBasis | null;
  categoryPath: readonly string[];
}): boolean {
  return Boolean(document.statementBasis) || isFinancialStatementPath(document.categoryPath);
}
